/**
 * Der Inhalt eines **Texts** — eine HTML-Teilmenge, wie TipTap sie schreibt
 * (ADR 0029): `<p>`, `<strong>`, `<em>`, `<u>`, `<s>`, `<mark data-color>`,
 * `<span data-color data-font data-size>`, `<ul><li>`, `<br>`.
 *
 * **Gelesen wird über eine Whitelist.** Aus dem HTML wird ein schlichter Baum;
 * was er nicht kennt, fällt weg — Skripte samt Inhalt, alle Attribute außer
 * den drei `data-*`, jeder Wert, der kein bekannter Schlüssel ist. Gerendert
 * wird dieser Baum, nie das HTML selbst: Es gibt kein `innerHTML` auf der
 * Fläche.
 *
 * Ohne React-Bezug wie die übrigen `utils/`; `DOMParser` gibt es im Browser
 * und in jsdom.
 */

import { CANVAS_NOTE_COLORS } from './canvasNoteStyle'
import type { CanvasTextMarkerShapeSpec } from './canvasTextMarkerShape'
import { CANVAS_NOTE_FONTS, getCanvasNoteFont } from './canvasFonts'
import {
  DEFAULT_CANVAS_TEXT_HIGHLIGHT,
  clampCanvasTextSize,
  getCanvasTextHighlight,
  getCanvasTextInk,
  type CanvasTextHighlightKey,
} from './canvasText'

/**
 * Der Schlüssel für „wieder die Standard-Tinte bzw. -Schrift" an einer Stelle —
 * nötig, wenn der Grundwert des Texts NICHT der Standard ist, ein Wort aber
 * doch. `null` hieße dort „wie der Grundwert".
 */
export const CANVAS_TEXT_DEFAULT_KEY = 'default'

/** Was an einer Stelle gilt. Fehlt ein Wert, gilt der Grundwert des Texts. */
export interface CanvasTextMarks {
  bold?: true
  italic?: true
  underline?: true
  strike?: true
  highlight?: CanvasTextHighlightKey
  /** Die Formnummer des Markers (`data-look`, `canvasTextMarker.ts`). */
  look?: number
  color?: string
  font?: string
  size?: number
}

export type CanvasTextInline =
  | { kind: 'text'; text: string; marks: CanvasTextMarks }
  | { kind: 'break' }

export type CanvasTextBlock =
  | { kind: 'paragraph'; content: CanvasTextInline[] }
  | { kind: 'list'; items: CanvasTextInline[][] }

const COLOR_KEYS = new Set<string>([...CANVAS_NOTE_COLORS.map(color => color.key), CANVAS_TEXT_DEFAULT_KEY])
const FONT_KEYS = new Set<string>([...CANVAS_NOTE_FONTS.flatMap(font => (font.key ? [font.key] : [])), CANVAS_TEXT_DEFAULT_KEY])
const HIGHLIGHT_KEYS = new Set<string>(CANVAS_NOTE_COLORS.map(color => color.key))

/**
 * Welche **Schriftfarben** ein Text kennt und wie sie aussehen. Standard ist
 * die Palette der Pinnwand; der Projekttitel bringt seine eigene mit (Titel-
 * Palette mit Abstufungen, CONTEXT.md „Projekt-Kopf“). Marker, Schriften und
 * Größen teilen beide.
 */
export interface CanvasTextColorPalette {
  isColorKey: (value: string) => boolean
  /** Der CSS-Farbwert zu einem Schlüssel (auch `default`). */
  ink: (key: string) => string
  /** Ob eine eigene Schrift im Gewicht einer Überschrift steht (`titleWeight`) — der Projekttitel. */
  headingWeight?: boolean
  /**
   * Eine berechnete Form für den Marker (`canvasTextMarkerShape.ts`) statt der
   * festen Kappen der Pinnwand — der Projekttitel (gewünscht 2026-09-26).
   */
  markerShape?: CanvasTextMarkerShapeSpec
}

export const CANVAS_TEXT_PALETTE: CanvasTextColorPalette = {
  isColorKey: value => COLOR_KEYS.has(value),
  ink: key => getCanvasTextInk(key === CANVAS_TEXT_DEFAULT_KEY ? null : key),
}

/** Was samt Inhalt wegfällt — alles andere Unbekannte wird nur ausgepackt. */
const DROPPED = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'SVG', 'MATH'])

/** Ob der Inhalt überhaupt HTML ist — ein Klartext (ältere Zeile, Seed) wird zeilenweise gelesen. */
function looksLikeHtml(content: string): boolean {
  return /^\s*</.test(content)
}

/** Ob ein Wert ein bekannter Farbschlüssel einer Stelle ist (Palette oder `default`). */
export function isCanvasTextColorKey(value: unknown): value is string {
  return typeof value === 'string' && COLOR_KEYS.has(value)
}

/** Ob ein Wert ein bekannter Schriftschlüssel einer Stelle ist. */
export function isCanvasTextFontKey(value: unknown): value is string {
  return typeof value === 'string' && FONT_KEYS.has(value)
}

/** Eine gespeicherte Formnummer — nur eine ganze Zahl im bekannten Bereich, sonst keine. */
export function parseCanvasTextMarkerLook(value: string | null): number | null {
  if (value === null || !/^\d{1,4}$/.test(value)) return null
  return Number(value)
}

/** Ob ein Wert eine bekannte Marker-Farbe ist. */
export function isCanvasTextHighlightKey(value: unknown): value is CanvasTextHighlightKey {
  return typeof value === 'string' && HIGHLIGHT_KEYS.has(value)
}

function marksOf(element: Element, inherited: CanvasTextMarks, palette: CanvasTextColorPalette): CanvasTextMarks {
  const marks: CanvasTextMarks = { ...inherited }
  switch (element.tagName) {
    case 'STRONG':
    case 'B':
      marks.bold = true
      break
    case 'EM':
    case 'I':
      marks.italic = true
      break
    case 'U':
      marks.underline = true
      break
    case 'S':
    case 'DEL':
    case 'STRIKE':
      marks.strike = true
      break
    case 'MARK': {
      const color = element.getAttribute('data-color')
      // Ein Marker ohne bekannte Farbe bleibt ein Marker — in Gelb, der Farbe
      // eines Textmarkers schlechthin.
      marks.highlight = color && HIGHLIGHT_KEYS.has(color) ? color as CanvasTextHighlightKey : DEFAULT_CANVAS_TEXT_HIGHLIGHT
      const look = parseCanvasTextMarkerLook(element.getAttribute('data-look'))
      if (look === null) delete marks.look
      else marks.look = look
      break
    }
    case 'SPAN': {
      const color = element.getAttribute('data-color')
      if (color && palette.isColorKey(color)) marks.color = color
      const font = element.getAttribute('data-font')
      if (font && FONT_KEYS.has(font)) marks.font = font
      const size = Number(element.getAttribute('data-size'))
      if (element.hasAttribute('data-size') && Number.isFinite(size) && size > 0) marks.size = clampCanvasTextSize(size)
      break
    }
  }
  return marks
}

function readInline(node: Node, marks: CanvasTextMarks, out: CanvasTextInline[], palette: CanvasTextColorPalette): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? ''
    if (text) out.push({ kind: 'text', text, marks })
    return
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return
  const element = node as Element
  if (DROPPED.has(element.tagName)) return
  if (element.tagName === 'BR') {
    out.push({ kind: 'break' })
    return
  }
  const next = marksOf(element, marks, palette)
  for (const child of Array.from(element.childNodes)) readInline(child, next, out, palette)
}

/** Ein Listeneintrag: seine Absätze nebeneinander, getrennt durch einen Umbruch. */
function readListItem(item: Element, palette: CanvasTextColorPalette): CanvasTextInline[] {
  const out: CanvasTextInline[] = []
  for (const child of Array.from(item.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'P') {
      if (out.length > 0) out.push({ kind: 'break' })
      readInline(child, {}, out, palette)
    } else if (!(child.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes((child as Element).tagName))) {
      readInline(child, {}, out, palette)
    }
  }
  return out
}

/** Die Werte einer Stelle als Stile — Ruhe (`CanvasTextContent`) und Editor (Dekoration) teilen sie. */
export interface CanvasTextInlineStyle {
  '--highlight'?: string
  /** Der Marker-Ausgleich der Schrift (`CanvasNoteFont.markerShift`). */
  '--marker-font-shift'?: string
  /** Wo das Durchstreichen und das Unterstreichen dieser Schrift sitzen (`CanvasNoteFont`). */
  '--strike-shift'?: string
  '--underline-depth'?: string
  color?: string
  fontFamily?: string
  fontWeight?: number
  fontSize?: string
}

/**
 * Was eine Stelle über den Grundwert hinaus trägt, als Stile. Fett, kursiv &
 * Co. sind Klassen bzw. Tags und stehen hier nicht — bis auf das Gewicht:
 * Eine eigene Schrift bringt ihr Gewicht mit, und als Inline-Stil schlüge das
 * sonst das Fett.
 */
export function canvasTextInlineStyle(marks: CanvasTextMarks, palette: CanvasTextColorPalette = CANVAS_TEXT_PALETTE): CanvasTextInlineStyle {
  const style: CanvasTextInlineStyle = {}
  // Die Farbe des **Markers** — gezeichnet wird er als eigene Ebene
  // (`.text-node__marker`, `canvasTextMarker.ts`), nicht als Hintergrund.
  if (marks.highlight) style['--highlight'] = getCanvasTextHighlight(marks.highlight)
  if (marks.color) style.color = palette.ink(marks.color)
  const font = marks.font ? getCanvasNoteFont(marks.font === CANVAS_TEXT_DEFAULT_KEY ? null : marks.font) : null
  if (font) {
    style.fontFamily = font.stack
    style['--marker-font-shift'] = `${font.markerShift}em`
    style['--strike-shift'] = `${font.strikeShift}em`
    style['--underline-depth'] = `${font.underlineDepth}em`
    style.fontWeight = marks.bold ? 700 : palette.headingWeight ? (font.projectTitleWeight ?? font.titleWeight) : font.weight
  }
  // Die Größe einer Stelle geht durch denselben Ausgleich wie der Grundwert
  // (`--text-font-scale`, `--text-live-scale`, siehe `TextNode.scss`).
  if (marks.size) style.fontSize = `calc(${marks.size}px * ${font ? font.scale : 'var(--text-font-scale, 1)'} * var(--text-live-scale, 1))`
  return style
}

/**
 * Der Inhalt als Blöcke. Ein leerer oder rein weißer Inhalt ergibt keine.
 *
 * @example
 * parseCanvasText('<p>Hallo <strong>Welt</strong></p>')
 * // [{ kind: 'paragraph', content: [{ kind: 'text', text: 'Hallo ', marks: {} },
 * //                                 { kind: 'text', text: 'Welt', marks: { bold: true } }] }]
 */
export function parseCanvasText(content: string, palette: CanvasTextColorPalette = CANVAS_TEXT_PALETTE): CanvasTextBlock[] {
  if (!content) return []
  if (!looksLikeHtml(content)) {
    return content.split('\n').map(line => ({
      kind: 'paragraph' as const,
      content: line ? [{ kind: 'text' as const, text: line, marks: {} }] : [],
    }))
  }
  const body = new DOMParser().parseFromString(content, 'text/html').body
  const blocks: CanvasTextBlock[] = []
  let loose: CanvasTextInline[] | null = null
  for (const child of Array.from(body.childNodes)) {
    const element = child.nodeType === Node.ELEMENT_NODE ? child as Element : null
    if (element && (element.tagName === 'UL' || element.tagName === 'OL')) {
      loose = null
      const items = Array.from(element.children).filter(item => item.tagName === 'LI').map(item => readListItem(item, palette))
      blocks.push({ kind: 'list', items })
      continue
    }
    if (element && (element.tagName === 'P' || element.tagName === 'DIV' || /^H[1-6]$/.test(element.tagName))) {
      loose = null
      const content: CanvasTextInline[] = []
      readInline(element, {}, content, palette)
      blocks.push({ kind: 'paragraph', content })
      continue
    }
    // Was frei zwischen den Blöcken steht, sammelt sich in einem Absatz.
    if (!loose) {
      loose = []
      blocks.push({ kind: 'paragraph', content: loose })
    }
    readInline(child, {}, loose, palette)
  }
  return blocks
}

function inlinePlain(content: CanvasTextInline[]): string {
  return content.map(part => (part.kind === 'break' ? '\n' : part.text)).join('')
}

/**
 * Der Inhalt als Klartext — für Karte, Anriss und alles, was nur lesen will.
 * Absätze und Listeneinträge stehen je auf einer Zeile.
 */
export function canvasTextPlain(content: string): string {
  return parseCanvasText(content)
    .flatMap(block => (block.kind === 'list' ? block.items.map(inlinePlain) : [inlinePlain(block.content)]))
    .join('\n')
}

/** Ob ein Text nichts Lesbares enthält — dann gibt es ihn in Ruhe nicht (CONTEXT.md, Text). */
export function isCanvasTextEmpty(content: string): boolean {
  return canvasTextPlain(content).trim() === ''
}
